import { DOCUMENT } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { AppConfigService } from './app-config.service';

export interface Branding {
    business_name: string;
    logo_url: string | null;
    primary_color: string;
    secondary_color: string;
}

const DEFAULT_BRANDING: Branding = {
    business_name: 'Comanda',
    logo_url: null,
    primary_color: '#4FD1C5',
    secondary_color: '#285E61',
};

/**
 * Fetchea la configuración de branding desde el backend al bootstrap y la aplica
 * via CSS variables + document.title. Se inicializa via provideAppInitializer.
 *
 * Los componentes leen `businessName`, `logoUrl`, etc. como signals.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly appConfig = inject(AppConfigService);
    private readonly doc = inject(DOCUMENT);
    private readonly titleService = inject(Title);
    private readonly http = inject(HttpClient);

    private readonly brandingSignal = signal<Branding>(DEFAULT_BRANDING);

    readonly branding = this.brandingSignal.asReadonly();
    readonly businessName = () => this.brandingSignal().business_name;
    readonly logoUrl = () => this.brandingSignal().logo_url;
    readonly primaryColor = () => this.brandingSignal().primary_color;
    readonly secondaryColor = () => this.brandingSignal().secondary_color;

    async load(): Promise<void> {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const apiUrl = this.appConfig.apiUrl;

        // Detector de race condition: si la página corre en un host real
        // pero apiUrl quedó en localhost, AppConfigService no terminó de
        // cargar /config.json antes de que BrandingService disparara este
        // load. Logueamos para que se detecte en consola/Sentry.
        const pageHost = this.doc.defaultView?.location?.hostname ?? '';
        const looksLocal = /localhost|127\.0\.0\.1/i.test(apiUrl);
        if (looksLocal && pageHost && pageHost !== 'localhost' && pageHost !== '127.0.0.1') {
            console.warn(
                `[BrandingService] apiUrl='${apiUrl}' apunta a localhost pero la página está en '${pageHost}'. ` +
                `AppConfigService probablemente no terminó de cargar /config.json antes de este fetch — ` +
                `verifica que ambos initializers corran secuencialmente en app.config.ts.`
            );
        }

        try {
            const response = await fetch(`${apiUrl}/settings/branding`, {
                headers: { 'Accept': 'application/json' },
                cache: 'no-cache',
            });
            if (!response.ok) {
                return;
            }
            const data = (await response.json()) as Partial<Branding>;
            const merged: Branding = { ...DEFAULT_BRANDING, ...data };
            this.brandingSignal.set(merged);
            this.applyBranding(merged);
        } catch {
            // Red caída o API no disponible: se queda con defaults
        }
    }

    /**
     * Actualiza el branding via PUT /settings/branding (PATCH semantics en backend).
     * Aplica los cambios inmediatamente a signals + CSS vars + title.
     */
    async update(partial: Partial<Branding>): Promise<Branding> {
        const updated = await firstValueFrom(
            this.http.put<Branding>(`${this.appConfig.apiUrl}/settings/branding`, partial)
        );
        const merged: Branding = { ...DEFAULT_BRANDING, ...updated };
        this.brandingSignal.set(merged);
        this.applyBranding(merged);
        return merged;
    }

    private applyBranding(branding: Branding): void {
        // Actualiza <title> del documento
        this.titleService.setTitle(branding.business_name);

        // Aplica CSS custom properties que PrimeNG y los estilos propios usan
        const root = this.doc.documentElement;
        root.style.setProperty('--brand-primary', branding.primary_color);
        root.style.setProperty('--brand-secondary', branding.secondary_color);
        root.style.setProperty('--p-primary-color', branding.primary_color);
    }
}
